import { useCallback, useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { IconCamera, IconTrash } from '@tabler/icons-react';
import {
  Avatar,
  Button,
  Group,
  Input,
  Stack,
  Text,
  WP_ICON_NAV,
  WP_IMAGE_FILE_ACCEPT,
  assignFilesToInput,
  useClipboardImagePaste,
} from '@wapharma/design-system';
import { apiDelete, apiPatch, apiPostForm } from '../api';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { AvatarCropModal } from './AvatarCropModal';
import styles from './EditProfileModal.module.css';

/** Profile form — hosted inside the settings overlay (was EditProfileModal). */
export function ProfileSettingsSection() {
  const { user, refreshUser } = useAuth();
  const { showToast } = useToast();
  const token = getToken();
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [pictureBusy, setPictureBusy] = useState(false);
  const [savePending, setSavePending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
    setErr(null);
    setSavePending(false);
  }, [user]);

  const closeCrop = () => {
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const openCropFromFile = useCallback((file: File) => {
    if (pictureBusy) return;
    const next = URL.createObjectURL(file);
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return next;
    });
  }, [pictureBusy]);

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0] ?? null;
    e.currentTarget.value = '';
    if (!file || pictureBusy) return;
    openCropFromFile(file);
  };

  const busy = pictureBusy || savePending;

  const { wrapProps } = useClipboardImagePaste({
    disabled: busy,
    accept: WP_IMAGE_FILE_ACCEPT,
    onFile: (file) => {
      if (fileInputRef.current && assignFilesToInput(fileInputRef.current, [file])) return;
      openCropFromFile(file);
    },
  });

  const uploadPicture = async (file: File) => {
    if (!token || pictureBusy) return;
    setPictureBusy(true);
    setErr(null);
    const form = new FormData();
    form.append('file', file);
    try {
      await apiPostForm('/auth/me/profile-picture', form, token);
      showToast('Photo mise à jour.', { tone: 'success' });
      await refreshUser();
    } catch (e: unknown) {
      setErr(getDisplayError(e));
      showToast(getDisplayError(e), { tone: 'error' });
    } finally {
      setPictureBusy(false);
    }
  };

  const removePicture = async () => {
    if (!token || pictureBusy || !user?.profilePictureUrl) return;
    setPictureBusy(true);
    setErr(null);
    try {
      await apiDelete('/auth/me/profile-picture', token);
      showToast('Photo supprimée.', { tone: 'success' });
      await refreshUser();
    } catch (e: unknown) {
      setErr(getDisplayError(e));
      showToast(getDisplayError(e), { tone: 'error' });
    } finally {
      setPictureBusy(false);
    }
  };

  const save = async () => {
    if (!token || savePending) return;
    setSavePending(true);
    setErr(null);
    try {
      await apiPatch(
        '/auth/me',
        {
          firstName: firstName.trim() || null,
          lastName: lastName.trim() || null,
        },
        token,
      );
      showToast('Profil enregistré.', { tone: 'success' });
      await refreshUser();
    } catch (e: unknown) {
      setErr(getDisplayError(e));
      showToast(getDisplayError(e), { tone: 'error' });
    } finally {
      setSavePending(false);
    }
  };

  if (!user) return null;

  const label =
    [firstName.trim(), lastName.trim()].filter(Boolean).join(' ') || user.email;

  return (
    <>
      <Stack gap="md" style={{ maxWidth: 420, width: '100%', margin: '0 auto' }}>
        <div className={styles.avatarBlock} {...wrapProps}>
          <div className={styles.avatarWrap}>
            <Avatar label={label} src={user.profilePictureUrl} size="lg" className={styles.avatar} />
            <input
              ref={fileInputRef}
              id={fileInputId}
              type="file"
              accept={WP_IMAGE_FILE_ACCEPT}
              className={styles.visuallyHiddenFile}
              disabled={busy}
              onChange={onFileChange}
            />
            <label
              htmlFor={fileInputId}
              className={styles.cameraBtn}
              aria-label="Changer la photo. Coller une image avec Ctrl+V."
              aria-disabled={busy || undefined}
            >
              <IconCamera size={16} stroke={1.5} aria-hidden />
            </label>
          </div>
          {user.profilePictureUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void removePicture()}
              disabled={busy}
            >
              <span className={styles.removeLabel}>
                <IconTrash {...WP_ICON_NAV} aria-hidden />
                Supprimer la photo
              </span>
            </Button>
          ) : null}
        </div>

        <Input
          label="Prénom"
          value={firstName}
          onChange={(e) => setFirstName(e.currentTarget.value)}
          disabled={busy}
        />
        <Input
          label="Nom"
          value={lastName}
          onChange={(e) => setLastName(e.currentTarget.value)}
          disabled={busy}
        />
        <Input label="E-mail" value={user.email} disabled readOnly />
        <Text size="sm" c="secondary">
          Votre profil aide à vous reconnaître dans l’admin.
        </Text>
        {err ? (
          <Text size="sm" c="error">
            {err}
          </Text>
        ) : null}
        <Group justify="flex-end">
          <Button variant="primary" onClick={() => void save()} loading={savePending} disabled={busy}>
            Enregistrer
          </Button>
        </Group>
      </Stack>

      <AvatarCropModal
        opened={Boolean(cropSrc)}
        imageSrc={cropSrc}
        onClose={closeCrop}
        onConfirm={(file) => {
          void uploadPicture(file);
        }}
      />
    </>
  );
}
